/**
 * RawHtmlSelector hook — lets admins click on elements within rendered raw HTML
 * to send them to the AI assistant for editing.
 *
 * Supports two levels of granularity:
 * 1. Elements with `data-assistant-id` — individual elements (paragraphs, headings, etc.)
 * 2. Top-level children (sections) — fallback for elements without data-assistant-id
 *
 * Attach with phx-hook="RawHtmlSelector" on the raw HTML container div.
 * Requires data-post-id on the container element.
 */
export const RawHtmlSelector = {
  mounted() {
    this.postId = this.el.dataset.postId;
    this.postName = this.el.dataset.postName;
    this.setupSelectors();
  },

  updated() {
    this.postId = this.el.dataset.postId;
    this.postName = this.el.dataset.postName;
    this.teardownSelectors();
    this.setupSelectors();
  },

  destroyed() {
    this.teardownSelectors();
  },

  setupSelectors() {
    this._listeners = [];

    // Find all elements with data-assistant-id for granular selection
    const assistantElements = this.el.querySelectorAll("[data-assistant-id]");
    assistantElements.forEach((el) => {
      el.classList.add("raw-element-selectable");

      const handleClick = (e) => {
        // If clicking inside any actively-editing element, don't interfere
        if (e.target.closest(".inline-editing")) {
          e.stopPropagation();
          return;
        }
        if (el.classList.contains("inline-editing")) {
          e.stopPropagation();
          return;
        }

        // Ctrl/Cmd+click → inline edit (even for links/buttons)
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          e.stopPropagation();
          this.pushEvent("inline-edit", {
            post_id: this.postId,
            element_id: el.dataset.assistantId,
          });
          return;
        }

        // In the admin preview, clicking links/buttons selects them for AI
        // instead of following the public-page interaction.
        if (e.target.closest("input, textarea, select")) return;
        e.stopPropagation();
        e.preventDefault();

        this.pushEvent("ai-prefill", {
          post_id: this.postId,
          post_name: this.postName,
          element_id: el.dataset.assistantId,
          ...this.describeElement(el, e.target),
        });
      };

      const handleMouseEnter = (e) => {
        e.stopPropagation();
        // Remove hover from any parent selectable elements
        this.el
          .querySelectorAll(".raw-section-hover, .raw-element-hover")
          .forEach((other) =>
            other.classList.remove("raw-section-hover", "raw-element-hover"),
          );
        el.classList.add("raw-element-hover");
      };

      const handleMouseLeave = () => {
        el.classList.remove("raw-element-hover");
      };

      el.addEventListener("click", handleClick);
      el.addEventListener("mouseenter", handleMouseEnter);
      el.addEventListener("mouseleave", handleMouseLeave);

      this._listeners.push({
        el,
        handleClick,
        handleMouseEnter,
        handleMouseLeave,
      });
    });

    // Top-level children as fallback section selectors
    const children = Array.from(this.el.children);
    children.forEach((child) => {
      child.classList.add("raw-section-selectable");

      const handleClick = (e) => {
        if (e.target.closest(".inline-editing")) return;
        if (e.target.closest("a, button, input, textarea, select")) return;
        // Don't fire if a more specific data-assistant-id element handled it
        if (e.target.closest("[data-assistant-id]")) return;
        e.stopPropagation();
        this.pushEvent("ai-prefill", {
          post_id: this.postId,
          post_name: this.postName,
          selection_type: "section",
          element_label: "Section",
          element_preview: this.previewText(child),
        });
      };

      const handleMouseEnter = () => {
        // Only show section hover if not hovering a granular element
        if (!this.el.querySelector(".raw-element-hover")) {
          child.classList.add("raw-section-hover");
        }
      };

      const handleMouseLeave = () => {
        child.classList.remove("raw-section-hover");
      };

      child.addEventListener("click", handleClick);
      child.addEventListener("mouseenter", handleMouseEnter);
      child.addEventListener("mouseleave", handleMouseLeave);

      this._listeners.push({
        el: child,
        handleClick,
        handleMouseEnter,
        handleMouseLeave,
      });
    });
  },

  describeElement(el, target) {
    const interactive = target.closest("a, button");
    const selected = interactive || el;
    const tag = selected.tagName.toLowerCase();

    if (tag === "img") {
      return {
        selection_type: "image",
        element_label: selected.getAttribute("alt") || "Image",
        element_preview: selected.getAttribute("src") || "",
      };
    }

    if (tag === "button") {
      return {
        selection_type: "button",
        element_label: this.previewText(selected) || "Button",
        element_preview: this.previewText(selected),
      };
    }

    if (tag === "a") {
      const text = this.previewText(selected) || selected.getAttribute("href") || "Link";
      return {
        selection_type: "link",
        element_label: text,
        element_preview: selected.getAttribute("href") || text,
      };
    }

    if (selected.closest("form")) {
      return {
        selection_type: "form",
        element_label: "Form",
        element_preview: this.previewText(selected.closest("form")),
      };
    }

    if (/^h[1-6]$/.test(tag)) {
      return {
        selection_type: "heading",
        element_label: this.previewText(selected) || "Heading",
        element_preview: this.previewText(selected),
      };
    }

    return {
      selection_type: "text",
      element_label: this.previewText(selected) || "Text",
      element_preview: this.previewText(selected),
    };
  },

  previewText(el) {
    return (el?.innerText || el?.textContent || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 180);
  },

  teardownSelectors() {
    if (!this._listeners) return;

    this._listeners.forEach(
      ({ el, handleClick, handleMouseEnter, handleMouseLeave }) => {
        el.removeEventListener("click", handleClick);
        el.removeEventListener("mouseenter", handleMouseEnter);
        el.removeEventListener("mouseleave", handleMouseLeave);
        el.classList.remove(
          "raw-section-selectable",
          "raw-section-hover",
          "raw-element-selectable",
          "raw-element-hover",
        );
      },
    );

    this._listeners = [];
  },
};
